import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const languages = [
  { code: 'pt-BR', name: 'Português', flag: '🇧🇷' },
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
];

interface LanguageSwitcherProps {
  expanded?: boolean;
}

export default function LanguageSwitcher({ expanded = false }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="wa-icon-btn flex items-center"
        title={!expanded ? currentLanguage.name : undefined}
        style={{
          width: expanded ? '100%' : '40px',
          justifyContent: expanded ? 'flex-start' : 'center',
          padding: expanded ? '12px 16px' : '10px',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          borderRadius: '12px',
          minHeight: '44px'
        }}
      >
        <Globe className="h-5 w-5" style={{ flexShrink: 0 }} />
        {expanded && (
          <span className="ml-3 text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis" style={{ flex: 1, textAlign: 'left' }}>
            {t('nav.language')}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            className="absolute left-full bottom-0 ml-2 w-44 wa-dropdown z-50 py-1"
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className="wa-dropdown-item w-full flex items-center gap-3 text-left"
                style={{ color: 'var(--text-primary)' }}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="text-sm flex-1">{lang.name}</span>
                {currentLanguage.code === lang.code && (
                  <Check className="h-4 w-4" style={{ color: 'var(--wa-green)' }} />
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
